import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { apiFetch, useAuth } from '../App';

/**
 * Global background notifier for song generation status changes.
 * Polls /api/songs every ~25 seconds while the user is logged in,
 * and fires a toast the moment any of their songs flips from
 * "processing" → "completed" or "failed". Lets users freely navigate
 * the site while songs render, instead of staring at a waiting screen.
 *
 * - Tracks "known" song statuses in a ref + localStorage so toasts
 *   don't fire repeatedly across page reloads.
 * - Skips firing when the document is hidden (browser tab in background)
 *   so the user gets a fresh notification on return rather than missing it.
 */
const STORAGE_KEY = 'wim_song_status_snapshot';
const POLL_INTERVAL_MS = 25000;

export default function SongCompletionNotifier() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const knownStatusesRef = useRef(loadSnapshot());
  const initializedRef = useRef(false);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    if (!user) {
      // User logged out — clear snapshot to avoid stale toasts on next login
      knownStatusesRef.current = {};
      initializedRef.current = false;
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
      return;
    }

    let cancelled = false;

    const checkSongs = async () => {
      try {
        const { data } = await apiFetch('/songs');
        if (cancelled || !Array.isArray(data)) return;

        const previous = knownStatusesRef.current || {};
        const next = {};

        // First pass: build the next snapshot
        data.forEach((s) => {
          if (s?.song_id) next[s.song_id] = s.status;
        });

        // On initial load we just record the snapshot — never fire toasts
        // for songs that were already completed/failed before the page loaded.
        if (!initializedRef.current) {
          initializedRef.current = true;
          knownStatusesRef.current = next;
          saveSnapshot(next);
          return;
        }

        // Second pass: detect transitions
        data.forEach((song) => {
          const id = song?.song_id;
          if (!id) return;
          const prev = previous[id];
          const curr = song.status;

          if (prev === 'processing' && curr === 'completed') {
            toast.success(`🎵 "${song.title || 'Your song'}" is ready!`, {
              duration: 8000,
              action: {
                label: 'Listen',
                onClick: () => navigate(`/song/${id}`),
              },
            });
          } else if (prev === 'processing' && curr === 'failed') {
            toast.error(`"${song.title || 'Your song'}" failed to generate`, {
              duration: 8000,
              description: 'No credits deducted. You can try again anytime.',
              action: {
                label: 'View',
                onClick: () => navigate(`/song/${id}`),
              },
            });
          }
        });

        knownStatusesRef.current = next;
        saveSnapshot(next);
      } catch (err) {
        // Silently ignore — we'll retry on the next interval
        // (network blips shouldn't spam the user with errors)
      }
    };

    // Run once immediately to establish baseline, then on interval
    checkSongs();
    pollTimerRef.current = setInterval(checkSongs, POLL_INTERVAL_MS);

    // Also re-check on tab focus so users see updates instantly when they
    // come back to the tab
    const onFocus = () => checkSongs();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, navigate]);

  return null;
}

function loadSnapshot() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch (_) {
    return {};
  }
}

function saveSnapshot(snap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch (_) { /* ignore quota errors */ }
}
